import { createUseStyles } from 'react-jss';
import { lighten, darken } from '@wds/utils';

const useStyles = createUseStyles((theme) => {
  const {
    bgColor,
    button,
    fontFamily,
    fontWeight,
    semanticColor,
    spacing,
    typeColor,
  } = theme;

  const variantSolid = (textColor, color, isPressed) => ({
    color: textColor,
    borderColor: isPressed,
    backgroundColor: isPressed,

    '&:hover, &:focus': {
      borderColor: lighten(0.03, color),
      backgroundColor: lighten(0.03, color),
    },

    '&:active': {
      borderColor: color,
      backgroundColor: color,
    },
  });

  const variantGhost = (backgroundColor, isPressed) => ({
    color: isPressed,
    borderColor: isPressed,
    backgroundColor: 'transparent',

    '&:hover, &:focus': {
      backgroundColor,
    },

    '&:active': {
      borderColor: isPressed,
    },
  });

  const variantText = (backgroundColor, isPressed) => ({
    color: isPressed,
    borderColor: 'transparent',
    backgroundColor: 'transparent',

    '&:hover, &:focus': {
      borderColor: backgroundColor,
      backgroundColor,
    },
  });

  const svgMargin = (leftIcon, rightIcon, size) => {
    const iconMargin = (s) => {
      return {
        sm: '0.5em',
        md: '0.8em',
        lg: '1em',
      }[s];
    };

    return {
      '& > svg': {
        ...(leftIcon && {
          marginRight: `${iconMargin(size)}`,
        }),
        ...(rightIcon && {
          marginLeft: `${iconMargin(size)}`,
        }),
      },
    };
  };

  const iconPadding = (size) => {
    return {
      sm: spacing[2],
      md: spacing[4],
      lg: spacing[5],
    }[size];
  };

  return {
    wdsButton: ({
      color,
      variant,
      size,
      pressed,
      stretched,
      iconOnly,
      leftIcon,
      rightIcon,
    }) => {
      const buttonColor = semanticColor[color];
      const isPressed = pressed ? darken(0.03, buttonColor) : buttonColor;

      const isStretched = stretched
        ? { display: 'flex', width: '100%' }
        : { display: 'inline-flex' };

      return {
        ...isStretched,
        fontWeight: fontWeight.medium,
        fontFamily: fontFamily.base,
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        position: 'relative',
        borderRadius: button.borderRadius,
        textDecoration: 'none',
        border: '1px solid transparent',
        boxShadow: button.boxShadow,
        transition: 'color 0.4s, border-color 0.4s, background-color 0.4s',

        padding: `0 ${iconOnly ? iconPadding(size) : button.padding[size]}`,
        fontSize: button.fontSize[size],
        height: button.height[size],

        '&:hover, &:focus': {
          transition: 'none',
          textDecoration: 'none',
        },

        '&:active': {
          transition: 'none',
          outline: 'none',
        },

        '&[disabled]': {
          pointerEvents: 'none',
          opacity: '0.4',
        },

        ...(variant === 'solid' &&
          variantSolid(typeColor.on[color], buttonColor, isPressed)),

        ...(variant === 'ghost' && variantGhost(bgColor.accent, isPressed)),

        ...(variant === 'text' && variantText(bgColor.accent, isPressed)),

        ...((leftIcon || rightIcon) && svgMargin(leftIcon, rightIcon, size)),
      };
    },
  };
});

export default useStyles;
